/**
 * Loads the tenant's custom stat fields AND its play-entry enum catalog for
 * the Play Entry form.
 *
 * One fetch per mount for the whole catalog (`applies_to` is filtered in the
 * browser by `customFieldsForPhase`), because an operator flips the O/D/ST
 * toggle constantly during a game and a refetch per toggle would put a network
 * round trip between the snap and the keyboard.
 *
 * The same response carries `enum_categories` — the tenant's live FORMATION /
 * COVERAGE / FRONT / PERSONNEL / MOTION / PROTECTION values, straight from
 * `enum_values` with `is_deprecated = false`. That is the SAME set the plays
 * service validates a submission against, so the form can no longer offer a
 * value the API will reject. Before this, the form shipped a hardcoded
 * `DEFAULT_STAT_CATALOG` which drifted ahead of the per-tenant catalog: the
 * 0032 stock list reached the UI while tenants seeded before it still held the
 * old vocabulary, so Play Entry offered "Ace" and the save 422'd.
 *
 * Failure is non-fatal by design: if the registry is unreachable the built-in
 * fields still work, the platform defaults stand in for the catalog, and
 * `isUnavailable` lets the form say so quietly. Play Entry must never be
 * blocked by an optional catalog.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';

import { useApiClient } from '../../app/providers';
import type { StatFieldDefinition } from '../../lib/api-types';

import { DEFAULT_STAT_CATALOG, type StatCatalog } from './play-catalog';

export interface CustomStatFieldsState {
  fields: readonly StatFieldDefinition[];
  /**
   * The tenant's enum catalog. Falls back to {@link DEFAULT_STAT_CATALOG} until
   * the fetch resolves, and stays on it if the fetch fails — a form with the
   * platform defaults beats a form with empty dropdowns.
   */
  catalog: StatCatalog;
  isLoading: boolean;
  isUnavailable: boolean;
  /** Refetch the registry and catalog (after a catalog edit). */
  refresh: () => void;
}

/** The registry is small (tens of rows); one page is the whole catalog. */
const PAGE_LIMIT = 200;

/**
 * Server `enum_categories` → the shape the field components consume.
 *
 * A category the tenant has no rows for keeps its platform default rather than
 * rendering an empty select: an unseeded tenant (or one mid-migration) can
 * still record a play. Categories the server does not know about are ignored —
 * the six in {@link StatCatalog} are the ones Play Entry renders.
 */
function toStatCatalog(
  categories: readonly {
    category: string;
    values: readonly { value: string; label: string; glyph_svg?: string | null }[];
  }[]
): StatCatalog {
  const next: StatCatalog = { ...DEFAULT_STAT_CATALOG };
  for (const entry of categories) {
    const key = entry.category as keyof StatCatalog;
    if (!(key in next) || entry.values.length === 0) continue;
    next[key] = entry.values.map((option) => ({
      value: option.value,
      label: option.label,
      glyphSvg: option.glyph_svg ?? null,
    }));
  }
  return next;
}

export interface UseCustomStatFieldsOptions {
  /**
   * `true` also returns retired fields. Play Entry leaves this off (a retired
   * field must not be offered); the Play Archive turns it on, because the values
   * a retired field already collected are still part of the game record.
   */
  includeDisabled?: boolean;
}

export function useCustomStatFields(
  options: UseCustomStatFieldsOptions = {}
): CustomStatFieldsState {
  const { includeDisabled = false } = options;
  const apiClient = useApiClient();
  const [fields, setFields] = useState<readonly StatFieldDefinition[]>([]);
  const [catalog, setCatalog] = useState<StatCatalog>(DEFAULT_STAT_CATALOG);
  const [isLoading, setIsLoading] = useState(true);
  const [isUnavailable, setIsUnavailable] = useState(false);
  // Bumped by `refresh()` after the catalog changes (a formation was turned
  // on or off from the board's Config panel) so the buttons follow.
  const [generation, setGeneration] = useState(0);
  const refresh = useCallback(() => setGeneration((current) => current + 1), []);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);

    apiClient
      .listStatFields(
        {
          include_builtin: false,
          include_disabled: includeDisabled ? true : undefined,
          limit: PAGE_LIMIT,
        },
        controller.signal
      )
      .then((page) => {
        if (controller.signal.aborted) return;
        setFields(page.items);
        setCatalog(toStatCatalog(page.enum_categories ?? []));
        setIsUnavailable(false);
      })
      .catch(() => {
        if (controller.signal.aborted) return;
        setFields([]);
        // Keep the platform defaults: an operator mid-drive needs a usable
        // form far more than a strictly accurate one.
        setCatalog(DEFAULT_STAT_CATALOG);
        setIsUnavailable(true);
      })
      .finally(() => {
        if (!controller.signal.aborted) setIsLoading(false);
      });

    return () => controller.abort();
  }, [apiClient, includeDisabled, generation]);

  return useMemo(
    () => ({ fields, catalog, isLoading, isUnavailable, refresh }),
    [fields, catalog, isLoading, isUnavailable, refresh]
  );
}
